import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntil } from "rxjs/operators";
import { Bank, NotificationService, User, UserService } from "@beautyapp/core";
import { Subject } from "rxjs";
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from "@ngxs/store";
import { format } from "date-fns";
import { NzFormModule } from 'ng-zorro-antd/form';


import { NzButtonModule } from 'ng-zorro-antd/button';
import { DatepickerComponent, InputComponent, SelectComponent, TextareaComponent } from '@beautyapp/form';

@Component({
    selector: 'beautyapp-user-minute-balance',
    templateUrl: './user-minute-balance.component.html',
    styleUrls: ['./user-minute-balance.component.scss'],
  imports: [
    NzFormModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NzButtonModule,
    DatepickerComponent,
    SelectComponent,
    InputComponent,
    TextareaComponent
  ]
})
export class UserMinuteBalanceComponent implements OnInit, OnDestroy {
  @Input() user: User = {} as User;

  @Output() balanceChange = new EventEmitter<number>();

  private service: UserService = inject(UserService);
  private notificationService: NotificationService = inject(NotificationService);
  private translateService: TranslateService = inject(TranslateService);
  private store: Store = inject(Store);

  balanceForm: UntypedFormGroup = new UntypedFormGroup({
    operationType: new UntypedFormControl('BALANCEIN', Validators.required),
    transactionDate: new UntypedFormControl(new Date(), Validators.required),
    minutes: new UntypedFormControl(null, Validators.required),
    // beaut-517: null, not false — the operator has to choose, and defaulting
    // to a mode would put the decision back in the system's hands.
    withMoney: new UntypedFormControl(null, Validators.required),
    paymentType: new UntypedFormControl(null, Validators.required),
    cash: new UntypedFormControl(null),
    card: new UntypedFormControl(null),
    bankTransfer: new UntypedFormControl(null),
    balance: new UntypedFormControl(null),
    barter: new UntypedFormControl(null),
    // beaut-386: bank for card/split/transfer credits, so the minute top-up
    // records bankId like the amount top-up (beaut-307). Same addBalanceNew path.
    bank: new UntypedFormControl(null),
    comment: new UntypedFormControl(null)
  });

  addBalanceLoader: boolean = false;

  // beaut-386: bank options for card/split/transfer minute credits. Labels match
  // the amount top-up form (user-balance-modal) so both pickers read identically.
  readonly banks: { value: Bank; label: string }[] = [
    { value: Bank.TBC, label: 'TBC Bank' },
    { value: Bank.BOG, label: 'Bank of Georgia' },
    { value: Bank.Credo, label: 'Credo Bank' },
    { value: Bank.Tera, label: 'Tera Bank' },
    { value: Bank.Cartu, label: 'Cartu Bank' },
    { value: Bank.Liberty, label: 'Liberty Bank' },
    { value: Bank.ProCredit, label: 'ProCredit Bank' },
    { value: Bank.SilkRoad, label: 'Silk Road Bank' },
    { value: Bank.BasisBank, label: 'Basis Bank' },
    { value: Bank.PashaBank, label: 'Pasha Bank' },
    { value: Bank.Other, label: 'Other' },
  ];

  roles = this.store.selectSnapshot((state) => state.auth.roles);
  maxDate = new Date();

  sub$: Subject<any> = new Subject<any>();



  ngOnInit(): void {
    // beaut-517: switching to "without money" must leave nothing behind. A
    // hidden control still submits its value, and the backend now rejects a
    // stray amount — so the payment type and every money field are cleared,
    // not merely hidden.
    this.balanceForm.get('withMoney')?.valueChanges.subscribe((withMoney) => {
      const paymentType = this.balanceForm.get('paymentType');
      if (withMoney) {
        paymentType?.setValidators(Validators.required);
      } else {
        paymentType?.clearValidators();
        paymentType?.setValue(null);
        ['cash', 'card', 'bankTransfer', 'barter', 'balance', 'bank'].forEach((name) => {
          const control = this.balanceForm.get(name);
          control?.clearValidators();
          control?.setValue(null);
          control?.updateValueAndValidity();
        });
      }
      paymentType?.updateValueAndValidity();
    });

    this.balanceForm.get('paymentType')?.valueChanges.subscribe((value) => {
      if (value === 1) {
        this.balanceForm.get('cash')?.setValidators(Validators.required);
        this.balanceForm.get('card')?.setValidators(null);
        this.balanceForm.get('bankTransfer')?.setValidators(null);
        this.balanceForm.get('balance')?.setValidators(null);
      } else if (value === 2) {
        this.balanceForm.get('cash')?.setValidators(null);
        this.balanceForm.get('card')?.setValidators(Validators.required);
        this.balanceForm.get('bankTransfer')?.setValidators(null);
        this.balanceForm.get('balance')?.setValidators(null);
      } else if (value === 3) {
        this.balanceForm.get('cash')?.setValidators(null);
        this.balanceForm.get('card')?.setValidators(null);
        this.balanceForm.get('bankTransfer')?.setValidators(null);
        this.balanceForm.get('balance')?.setValidators(null);
      } else if (value === 8) {
        this.balanceForm.get('cash')?.setValidators(null);
        this.balanceForm.get('card')?.setValidators(null);
        this.balanceForm.get('balance')?.setValidators(null);
        this.balanceForm.get('bankTransfer')?.setValidators(Validators.required);
      } else {
        this.balanceForm.get('cash')?.setValidators(null);
        this.balanceForm.get('card')?.setValidators(null);
        this.balanceForm.get('bankTransfer')?.setValidators(null);
      }
      // beaut-386: clear the bank when the payment type doesn't route through a
      // bank (card=2, split=3, transfer=8) so a stale bank never submits.
      if (![2, 3, 8].includes(value)) {
        this.balanceForm.get('bank')?.setValue(null);
      }
      this.balanceForm.get('cash')?.updateValueAndValidity();
      this.balanceForm.get('card')?.updateValueAndValidity();
      this.balanceForm.get('bankTransfer')?.updateValueAndValidity();
    })
  }

  addBalance(): void {
    this.balanceForm.markAllAsTouched()
    if (this.balanceForm.invalid) {
      return;
    }
    // beaut-517: with money the operator must have entered one. The backend
    // rejects a zero anyway; catching it here says so in their own language
    // instead of as a 400.
    const { withMoney, ...payload } = this.balanceForm.value;
    const money =
      +(this.balanceForm.value.cash || 0) +
      +(this.balanceForm.value.card || 0) +
      +(this.balanceForm.value.bankTransfer || 0);
    if (withMoney && money === 0) {
      this.notificationService.error(
        this.translateService.instant('users.moneyRequired'),
        this.translateService.instant('users.upBalance')
      );

      return;
    }
    this.addBalanceLoader = true;
    this.service
      .addBalance({
        // `withMoney` is a form field, not a request field — the mode travels
        // as `paymentType` being absent, which is why no column was added.
        ...payload,
        transactionDate: format(new Date(this.balanceForm.value.transactionDate), 'yyyy-MM-dd'),
        price: withMoney ? +this.balanceForm.value.cash + +this.balanceForm.value.card : 0,
        userId: this.user.id,
      })
      .pipe(takeUntil(this.sub$))
      .subscribe((res) => {
        if (res) {
          this.balanceChange.emit(res.balance);
          this.notificationService.success(
            this.translateService.instant('users.balanceFilledSuccessfully'),
            this.translateService.instant('users.upBalance')
          );
          this.addBalanceLoader = false;
          this.balanceForm.reset();
        }
      });
  }

  ngOnDestroy(): void {
    this.sub$.next(null);
    this.sub$.complete();
  }
}
